import { useCallback, useEffect, useState } from "react";
import { AxiosInstance } from "axios";
import { updateCarrierBridgeMeta } from "../requests/request";

interface UseCarrierBridgeMetaArgs {
  api: AxiosInstance;
  /** Null until the detail has loaded; nothing is saved before then. */
  carrierBridgeId: number | null;
  /** The row's own tenant, so a partner's edit lands on the customer's service. */
  tenantId: number;
  initialName: string;
  initialTags: string[];
}

/**
 * Name and tag editing for the Carrier Bridge detail. Carrier Bridge's own
 * copy of the shared `useMetaEditor`, trimmed to what the detail card uses,
 * so the shared hook and its services stay untouched.
 *
 * Each save sends only the field that changed; the backend PATCH accepts
 * either one alone.
 */
export function useCarrierBridgeMeta({
  api,
  carrierBridgeId,
  tenantId,
  initialName,
  initialTags,
}: UseCarrierBridgeMetaArgs) {
  const [nameValue, setNameValue] = useState(initialName);
  const [savedName, setSavedName] = useState(initialName);
  const [tags, setTags] = useState<string[]>(initialTags);
  const [savingField, setSavingField] = useState<"name" | "tag" | null>(null);

  // Re-seed when the detail (re)loads.
  useEffect(() => {
    setNameValue(initialName);
    setSavedName(initialName);
  }, [initialName]);
  useEffect(() => {
    setTags(initialTags);
  }, [initialTags]);

  const saveName = useCallback(
    async (nextName: string) => {
      if (carrierBridgeId == null) return;
      setNameValue(nextName);
      setSavingField("name");
      try {
        await updateCarrierBridgeMeta(api, carrierBridgeId, tenantId, {
          name: nextName,
        });
        setSavedName(nextName);
      } catch (err) {
        console.error("Error renaming Carrier Bridge", err);
        setNameValue(savedName);
      } finally {
        setSavingField(null);
      }
    },
    [api, carrierBridgeId, tenantId, savedName]
  );

  const cancelName = useCallback(() => setNameValue(savedName), [savedName]);

  // Rejects on failure so the tag modal keeps the user's edit open.
  const updateTags = useCallback(
    async (nextTags: string[]) => {
      if (carrierBridgeId == null) return;
      setSavingField("tag");
      try {
        await updateCarrierBridgeMeta(api, carrierBridgeId, tenantId, {
          tag: nextTags.join(", "),
        });
        setTags(nextTags);
      } finally {
        setSavingField(null);
      }
    },
    [api, carrierBridgeId, tenantId]
  );

  return { nameValue, tags, savingField, saveName, cancelName, updateTags };
}
